/**
 * Browser-side half of the Final Results PDF: turns the tile icons / round
 * photos / logo into JPEGs (via canvas), hands them to the pure layout in
 * `resultsPdf.ts`, and wires the Share and Download as PDF buttons.
 *
 * The PDF is built in the background as soon as the results screen renders,
 * not on tap. `navigator.share` has to be called while the tap's "user
 * activation" is still fresh, and stricter browsers (iOS Safari) let it lapse
 * within a moment or two — building a PDF from scratch on tap would routinely
 * outlast that. With the file already prepared, a tap shares immediately, and
 * Download as PDF opens its preview without a wait.
 */

import type { GameSummary } from '../game/summary';
import type { Game } from '../game/types';
import type { PdfDocument } from '../pdf/pdfWriter';
import type { Branding } from './branding';
import { buildResultsShareText } from './helpers';

// The PDF writer/layout (and its font-metric tables) are only needed once the
// Final Results screen is showing, so they load on demand rather than adding
// to the bundle every player downloads up front.
type ParseJpegInfo = typeof import('../pdf/pdfWriter').parseJpegInfo;

// Pixel widths pictures are scaled down to before embedding. Tiles are drawn
// ~20pt wide, so 90px stays sharp even zoomed in on a phone without bloating
// the file; photos are already downscaled by the app when captured.
const TILE_PX = 90;
const PHOTO_PX = 900;
const LOGO_PX = 400;

/** A JPEG data URL's raw bytes, or undefined if `src` isn't one the PDF writer can embed as-is. */
function decodeJpegDataUrl(src: string, parseJpegInfo: ParseJpegInfo): Uint8Array | undefined {
  const match = /^data:image\/jpe?g;base64,(.*)$/i.exec(src);
  if (!match) return undefined;
  try {
    const binary = atob(match[1]!);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const { components } = parseJpegInfo(bytes);
    return components === 1 || components === 3 ? bytes : undefined;
  } catch {
    return undefined;
  }
}

/** Any image URL/data URL → JPEG bytes on a white background (PDFs embed JPEG directly; PNG transparency would otherwise turn black). */
async function toJpeg(src: string, maxWidth: number, parseJpegInfo: ParseJpegInfo): Promise<Uint8Array> {
  const direct = decodeJpegDataUrl(src, parseJpegInfo);
  if (direct) return direct;

  const img = new Image();
  img.src = src;
  await img.decode();

  const naturalW = img.naturalWidth || maxWidth;
  const naturalH = img.naturalHeight || Math.round(maxWidth * 0.3);
  const scale = Math.min(1, maxWidth / naturalW);
  const w = Math.max(1, Math.round(naturalW * scale));
  const h = Math.max(1, Math.round(naturalH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d')!;
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, w, h);
  g.drawImage(img, 0, 0, w, h);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  if (!blob) throw new Error('Could not encode image');
  return new Uint8Array(await blob.arrayBuffer());
}

// Tile icons are static assets shared by every game — load each once per page life.
const tileCache = new Map<string, Promise<Uint8Array>>();
function loadTile(code: string, parseJpegInfo: ParseJpegInfo): Promise<Uint8Array> {
  let p = tileCache.get(code);
  if (!p) {
    p = toJpeg(`/tile-icons/${code}.png`, TILE_PX, parseJpegInfo);
    p.catch(() => tileCache.delete(code)); // a failed load (offline blip) shouldn't stick
    tileCache.set(code, p);
  }
  return p;
}

/** File-name-safe base name, shared by the PDF's filename and its share title. */
function documentName(game: Game): string {
  const date = new Date(game.endedAt ?? Date.now()).toISOString().slice(0, 10);
  return `Mahjong Table ${game.tableId} - ${date}`.replace(/[\\/:*?"<>|\x00-\x1f]/g, '-');
}

/**
 * A finished report: the PDF file (to save or share) and the laid-out
 * document it was written from (which the on-screen preview replays).
 */
export interface ResultsPdf {
  file: File;
  doc: PdfDocument;
}

/**
 * Builds the Final Results PDF. Any picture that fails to load is simply left
 * out (the layout falls back to a labelled box for a missing tile) — a report
 * with one plain tile is still far more useful than no report.
 */
export async function buildResultsPdfDocument(
  game: Game,
  summary: GameSummary,
  branding: Branding,
): Promise<ResultsPdf> {
  const [{ layoutResultsPdf, tilesUsedInGame }, { parseJpegInfo }] = await Promise.all([
    import('./resultsPdf'),
    import('../pdf/pdfWriter'),
  ]);
  const tiles = new Map<string, Uint8Array>();
  const photos = new Map<number, Uint8Array[]>();
  let logo: Uint8Array | undefined;

  await Promise.all([
    ...tilesUsedInGame(game).map(async (code) => {
      try {
        tiles.set(code, await loadTile(code, parseJpegInfo));
      } catch {
        /* left out */
      }
    }),
    ...game.rounds.map(async (round) => {
      const loaded: Uint8Array[] = [];
      for (const src of round.photos ?? []) {
        try {
          loaded.push(await toJpeg(src, PHOTO_PX, parseJpegInfo));
        } catch {
          /* left out */
        }
      }
      if (loaded.length > 0) photos.set(round.index, loaded);
    }),
    (async () => {
      if (!branding.logoDataUrl) return;
      try {
        logo = await toJpeg(branding.logoDataUrl, LOGO_PX, parseJpegInfo);
      } catch {
        /* left out */
      }
    })(),
  ]);

  const name = documentName(game);
  const doc = layoutResultsPdf({
    game,
    summary,
    brandName: branding.name,
    title: name,
    images: { logo, tiles, photos },
    created: new Date(),
  });
  const file = new File([doc.build() as BlobPart], `${name}.pdf`, { type: 'application/pdf' });
  return { file, doc };
}

// ---------------------------------------------------------------------------
// Sharing & saving
// ---------------------------------------------------------------------------

/** True when this browser can hand a PDF *file* to the share sheet (Android Chrome, iOS Safari, …). */
export function canSharePdfFiles(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [new File([], 'results.pdf', { type: 'application/pdf' })] });
  } catch {
    return false;
  }
}

/** True when there's any share sheet at all — the button is hidden otherwise (nothing sensible to fall back to). */
export function canShareAnything(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** "Share PDF" where a file can really be shared, plain "Share" where only the text recap can. */
export function shareButtonLabel(): string {
  return canSharePdfFiles() ? 'Share PDF' : 'Share';
}

/**
 * A Final Results PDF that starts building the moment it's created and can be
 * used by more than one button (Share and Download as PDF both hand out the
 * same report, so they can never disagree and it's only built once).
 */
export interface ResultsPdfHandle {
  /** The finished report, if the background build has completed. */
  ready(): ResultsPdf | undefined;
  /** Resolves with the report (starting the build if needed); rejects if it can't be built. */
  get(): Promise<ResultsPdf>;
}

/** Starts building the PDF right away — call once when the Final Results screen renders. */
export function prepareResultsPdf(game: Game, summary: GameSummary, branding: Branding): ResultsPdfHandle {
  let result: ResultsPdf | undefined;
  let building: Promise<ResultsPdf> | undefined;
  const get = (): Promise<ResultsPdf> => {
    building ??= buildResultsPdfDocument(game, summary, branding).then(
      (r) => (result = r),
      (err: unknown) => {
        building = undefined; // let a later tap try again
        throw err;
      },
    );
    return building;
  };
  get().catch(() => {
    /* surfaced only if the person actually taps a button that needs it */
  });
  return { ready: () => result, get };
}

/** Saves a file to the device via a temporary download link. */
export function saveFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Long enough for the browser to have started reading it, short enough not to leak the PDF for the page's life.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Hands the PDF to the share sheet; closing the sheet without choosing anything is not an error. */
async function sharePdfFile(file: File): Promise<void> {
  try {
    await navigator.share({ files: [file], title: file.name.replace(/\.pdf$/i, '') });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    throw err;
  }
}

/**
 * Shows the report on screen with Print / Share buttons (see
 * `pdfPreview.ts`). If the preview itself can't be shown for any reason, it
 * just saves the file instead, so the person still ends up with their PDF.
 */
export async function showResultsPreview(result: ResultsPdf): Promise<void> {
  try {
    const { openPdfPreview } = await import('./pdfPreview');
    await openPdfPreview({
      doc: result.doc,
      title: result.file.name,
      onShare: canSharePdfFiles() ? () => sharePdfFile(result.file) : undefined,
    });
  } catch {
    saveFile(result.file);
  }
}

/**
 * Wires a rendered Download as PDF button: opens the report for viewing first,
 * with Print (whose dialog also offers Save as PDF, into a folder of their
 * choosing) and Share on the preview — rather than dropping a file on
 * the device the moment it's tapped. If the PDF can't be built for any
 * reason, `printFallback` (the browser's print dialog, which is what this
 * button used to always do) runs instead, so the button never just fails.
 */
export function wireResultsDownload(
  button: HTMLButtonElement,
  pdf: ResultsPdfHandle,
  printFallback: () => void,
): void {
  const idleLabel = button.textContent ?? 'Download as PDF';
  button.addEventListener('click', () => {
    void (async () => {
      let result = pdf.ready();
      if (!result) {
        button.disabled = true;
        button.textContent = 'Preparing PDF…';
        try {
          result = await pdf.get();
        } catch {
          result = undefined;
        }
        button.disabled = false;
        button.textContent = idleLabel;
      }
      if (result) await showResultsPreview(result);
      else printFallback();
    })();
  });
}

/**
 * Builds a fresh report from the given data and shows its preview; resolves
 * false (so the caller can fall back to printing) if it couldn't be built. For
 * screens that re-fetch their data at tap time and therefore can't reuse a
 * report prepared when they rendered.
 */
export async function previewFreshResultsPdf(game: Game, summary: GameSummary, branding: Branding): Promise<boolean> {
  let result: ResultsPdf;
  try {
    result = await buildResultsPdfDocument(game, summary, branding);
  } catch {
    return false;
  }
  await showResultsPreview(result);
  return true;
}

/**
 * Wires a rendered Share button (see `shareButtonLabel`). Where files can be
 * shared, the PDF (prepared in the background — see `prepareResultsPdf`) is
 * shared on tap; anywhere else — or if building the PDF fails for any reason —
 * it shares the plain-text recap instead, so the button always does
 * something useful.
 */
export function wireResultsShare(
  button: HTMLButtonElement,
  game: Game,
  summary: GameSummary,
  pdf: ResultsPdfHandle,
): void {
  const idleLabel = button.textContent ?? 'Share';
  const title = documentName(game);
  const shareRecapText = () =>
    navigator.share({ title, text: buildResultsShareText(game, summary) }).catch(() => {
      /* dismissing the share sheet rejects — not an error worth surfacing */
    });

  if (!canSharePdfFiles()) {
    button.addEventListener('click', () => void shareRecapText());
    return;
  }

  button.addEventListener('click', () => {
    void (async () => {
      let file = pdf.ready()?.file;
      if (!file) {
        button.disabled = true;
        button.textContent = 'Preparing PDF…';
        try {
          file = (await pdf.get()).file;
        } catch {
          button.disabled = false;
          button.textContent = idleLabel;
          await shareRecapText();
          return;
        }
        button.disabled = false;
        button.textContent = idleLabel;
      }

      try {
        await navigator.share({ files: [file], title });
      } catch (err) {
        const name = err instanceof DOMException ? err.name : '';
        if (name === 'AbortError') return; // they closed the share sheet
        if (name === 'NotAllowedError') {
          // The tap's permission lapsed while the PDF was still being built —
          // it's ready now, so the very next tap shares instantly.
          button.textContent = 'PDF ready — tap again';
          window.setTimeout(() => {
            button.textContent = idleLabel;
          }, 4000);
          return;
        }
        await shareRecapText();
      }
    })();
  });
}
